"use client";

import React from "react";
import { PhoneCall, CalendarCheck, MapPin, Clock, ShieldCheck, ArrowRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
export default function CallToAction() {
  const shouldReduceMotion = useReducedMotion();
  const trustHighlights = [{
    icon: Clock,
    title: "Fast Response Time",
    desc: "Plumbers ready 24/7 across Jonesboro & Clayton County."
  }, {
    icon: ShieldCheck,
    title: "Licensed & Guaranteed",
    desc: "Clear upfront pricing and dependable craftsmanship."
  }, {
    icon: MapPin,
    title: "Local Plumbers",
    desc: "Rooted in Jonesboro with immediate neighborhood dispatch."
  }];
  return <section id="calltoaction" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-6xl">
        <div className="bg-card text-card-foreground rounded-2xl md:rounded-3xl border border-border shadow-xl p-8 sm:p-12 lg:p-16 relative overflow-hidden">
          {/* Subtle decorative background pattern */}
          <div aria-hidden="true" className="absolute -right-16 -top-16 w-80 h-80 rounded-full bg-primary/5 pointer-events-none blur-3xl" />
          <div aria-hidden="true" className="absolute -left-16 -bottom-16 w-80 h-80 rounded-full bg-accent/10 pointer-events-none blur-3xl" />

          <div className="relative z-10 max-w-3xl mx-auto text-center">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4
          }} className="flex justify-center mb-4">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold gap-1.5 uppercase tracking-wide">
                <Clock className="w-3.5 h-3.5 text-primary" />
                Available 24/7 in Jonesboro, GA
              </Badge>
            </motion.div>

            <motion.h2 initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: 0.1
          }} className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground text-balance">
              Need a Plumber in Jonesboro? Contact Us Today
            </motion.h2>

            <motion.p initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: 0.15
          }} className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              Plumbing emergencies do not wait. Our experienced local specialists arrive quickly with fully stocked trucks to diagnose and fix leaks, pipe breaks, water heaters, and drain clogs.
            </motion.p>

            {/* Dual CTAs */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: 0.2
          }} className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="w-full sm:w-auto h-12 px-8 text-base font-semibold shadow-md gap-2.5">
                <a href="tel:4708479076">
                  <PhoneCall className="w-5 h-5" />
                  Call (470) 847-9076
                </a>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto h-12 px-8 text-base font-semibold gap-2.5 bg-background hover:bg-muted">
                <Link to="/contact/">
                  <CalendarCheck className="w-5 h-5 text-primary" />
                  Request Service Online
                </Link>
              </Button>
            </motion.div>

            {/* Service Areas Link */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: 0.25
          }} className="mt-6 flex items-center justify-center gap-1 text-sm text-muted-foreground">
              <span>Serving Jonesboro and neighboring communities.</span>
              <Link to="/service-areas/" className="inline-flex items-center gap-1 font-semibold text-primary hover:underline ml-1">
                View all service areas
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </motion.div>
          </div>

          {/* Trust Highlights Cards */}
          <div className="mt-12 pt-10 border-t border-border grid grid-cols-1 md:grid-cols-3 gap-6">
            {trustHighlights.map((item, i) => {
            const IconComp = item.icon;
            return <div key={item.title} data-index={i} className="flex items-start gap-3.5 p-4 rounded-xl bg-background/60 border border-border/70">
                  <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
                    <IconComp className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">{item.title}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-normal">{item.desc}</p>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}